import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import { createActor, type ActorRef, type SnapshotFrom, type EventFromLogic } from 'xstate'
import { modalStateMachine } from './modalStateMachine'
import { eventBus } from '@/utils/eventBus'
import { v4 as uuidv4 } from 'uuid'
import type { ModalContext, ModalEvent, ModalState, ModalItem } from './types'

const registry: Record<string, any> = {}

export function registerModal(type: string, comp: any) {
  if (!registry[type]) registry[type] = markRaw(comp)
}

export function getModalComp(type: string) {
  return registry[type]
}

// 为“取消也 resolve”携带配置开关；并保存超时定时器，关闭时清理

const useModalManagerStore = defineStore('modalManager', {
  state: () => ({
    modals: [] as ModalItem[],
    globalConfig: {
      maxConcurrentModals: 3,
      defaultTimeout: 300000, // 5分钟
    },
  }),

  getters: {
    // 获取当前打开的弹窗数量
    openModalCount: (state) => state.modals.filter((m) => m.open).length,

    // 获取pending状态的弹窗
    pendingModals: (state) =>
      state.modals.filter((m) => {
        const snapshot = m.actor.getSnapshot()
        return snapshot.value === 'processing'
      }),

    // 检查是否可以打开新弹窗
    canOpenModal: (state) => (priority?: 'high' | 'normal' | 'low') => {
      const openCount = state.modals.filter((m) => m.open).length
      if (priority === 'high') return true
      return openCount < state.globalConfig.maxConcurrentModals
    },
  },

  actions: {
    // open 统一返回 { ok, data|reason }，可通过 options.resolveOnCancel 控制“取消也 resolve”
    async open<T = any>(
      type: string,
      payload: any,
      options: {
        priority?: 'high' | 'normal' | 'low'
        timeout?: number
        metadata?: Record<string, any>
        resolveOnCancel?: boolean
        resolveOnConfirm?: boolean
      } = {},
    ): Promise<{ ok: true; data: T } | { ok: false; reason: any }> {
      const modalId = uuidv4()

      // 并发保护（除 high 优先级外，超过上限则拒绝）
      if (!this.canOpenModal(options.priority)) {
        // 这里保持抛错，属于调用前置条件问题
        throw new Error('Cannot open modal: too many concurrent modals')
      }

      // 统一 resolve，不再依赖 reject（兼容性：_handleStateChange 也统一 resolve）
      return new Promise<{ ok: true; data: T } | { ok: false; reason: any }>((resolve) => {
        // 创建状态机实例
        const actor = createActor(modalStateMachine, {
          input: {
            modalId,
            type,
            payload: payload,
            retryCount: 0,
            maxRetries: 3,
            startTime: Date.now(),
          } as ModalContext,
        })

        // 监听状态变化
        actor.subscribe({
          next: (snapshot) => {
            this._handleStateChange(modalId, snapshot)
          },
          error: (error) => {
            const err = error as Error
            console.error(`[ModalManager] State machine error for ${modalId}:`, err)
          },
        })

        // 启动状态机
        actor.start()

        // 发送打开事件
        actor.send({
          type: 'OPEN',
          payload: { modalId, type, data: payload },
        })

        // 把 resolve 放进 ModalItem，_handleStateChange 里统一 resolve union 结果
        const modalItem: ModalItem = {
          id: modalId,
          type,
          payload: payload,
          open: true,
          actor,
          resolve, // 只存 resolve
          startTime: Date.now(),
          options: {
            resolveOnCancel: !!options.resolveOnCancel,
            resolveOnConfirm: !!options.resolveOnConfirm,
          }, // +++
        }

        this.modals.push(modalItem)

        // 设置超时（TIMEOUT 事件），并保存 timeoutId，关闭时清理
        const timeout = options.timeout || this.globalConfig.defaultTimeout
        modalItem.timeoutId = window.setTimeout(() => {
          actor.send({ type: 'TIMEOUT' })
        }, timeout)
      })
    },

    confirm(modalId: string, data: any): void {
      const modal = this.modals.find((m) => m.id === modalId)
      if (!modal) return

      modal.actor.send({ type: 'CONFIRM', data })
    },

    cancel(modalId: string, reason?: string): void {
      const modal = this.modals.find((m) => m.id === modalId)
      if (!modal) return

      modal.actor.send({ type: 'CANCEL', reason })
    },

    startOperation(modalId: string, operation: string): void {
      const modal = this.modals.find((m) => m.id === modalId)
      if (!modal) return

      modal.actor.send({ type: 'START_OPERATION', operation })
    },

    operationSuccess(modalId: string, result: any): void {
      const modal = this.modals.find((m) => m.id === modalId)
      if (!modal) return

      modal.actor.send({ type: 'OPERATION_SUCCESS', result })
    },

    operationFailed(modalId: string, error: any): void {
      const modal = this.modals.find((m) => m.id === modalId)
      if (!modal) return

      modal.actor.send({ type: 'OPERATION_FAILED', error })
    },

    retry(modalId: string): void {
      const modal = this.modals.find((m) => m.id === modalId)
      if (!modal) return

      modal.actor.send({ type: 'RETRY' })
    },

    forceClose(modalId: string): void {
      const modal = this.modals.find((m) => m.id === modalId)
      if (!modal) return

      modal.actor.send({ type: 'FORCE_CLOSE' })
    },

    closeAll(): void {
      const modalIds = this.modals.map((m) => m.id)
      modalIds.forEach((id) => this.forceClose(id))
    },

    // 获取弹窗统计
    getStats(): {
      total: number
      open: number
      pending: number
      completed: number
      failed: number
      averageLifetime: number
    } {
      const now = Date.now()
      const completedModals = this.modals.filter((m) => {
        const snapshot = m.actor.getSnapshot()
        return snapshot.value === 'closed'
      })

      const averageLifetime =
        completedModals.length > 0
          ? completedModals.reduce((sum, m) => sum + (now - m.startTime), 0) /
            completedModals.length
          : 0

      const pendingCount = this.modals.filter((m) => {
        const snapshot = m.actor.getSnapshot()
        return snapshot.value === 'processing'
      }).length

      const failedCount = this.modals.filter((m) => {
        const snapshot = m.actor.getSnapshot()
        return snapshot.value === 'error'
      }).length

      return {
        total: this.modals.length,
        open: this.openModalCount,
        pending: pendingCount,
        completed: completedModals.length,
        failed: failedCount,
        averageLifetime,
      }
    },

    _handleStateChange(modalId: string, snapshot: any): void {
      const modal = this.modals.find((m) => m.id === modalId)
      if (!modal) return

      const { value, context } = snapshot

      // keep reactive derived fields in sync so UI updates
      modal.uiState = value
      modal.pending = value === 'processing'
      modal.error = context?.error
      modal.retryCount = context?.retryCount ?? 0

      // 在进入 processing 时，立刻把 confirm 数据 resolve 给外部（不关闭弹窗）
      if (value === 'processing' && modal.resolve) {
        const result = { ok: true as const, data: { ...(context?.data ?? {}), modalId } }
        modal.resolve(result)
        // 防止后续 closed 时再次 resolve
        modal.resolve = undefined
      }

      // 关闭态统一 resolve union 结果；根据开关决定“取消是否也 ok=false resolve”
      if (value === 'closed') {
        modal.open = false

        // 优先 data；否则把错误/取消映射为 { ok:false, reason }
        const result =
          context?.data !== undefined
            ? { ok: true as const, data: context.data }
            : { ok: false as const, reason: context?.error ?? 'cancelled' }

        // 统一 resolve union（兼容：旧逻辑可通过 resolveOnCancel=false 强制使用 reject 分支——已废弃）
        if (modal.resolve) {
          if (modal.options?.resolveOnCancel) {
            modal.resolve(result)
          } else {
            // 为了前向兼容：确定时 ok=true 用 resolve，否则走 reject
            if (result.ok) modal.resolve(result)
            else modal.reject?.(result.reason)
          }
        }

        // 清理超时定时器
        if (modal.timeoutId) {
          clearTimeout(modal.timeoutId)
          modal.timeoutId = undefined
        }

        // 延迟销毁，留给外部动画/过渡
        setTimeout(() => {
          modal.actor.stop()
          this.modals = this.modals.filter((m) => m.id !== modalId)
        }, 1000)
      }

      // 发送事件总线事件
      eventBus.emit('modal.state_changed', {
        modalId,
        state: value,
        context: context,
        timestamp: Date.now(),
      })

      // 发送特定状态事件（保持原有分支）
      switch (value) {
        case 'open':
          eventBus.emit('modal.opened', {
            modalId,
            type: modal.type,
            payload: modal.payload,
            timestamp: Date.now(),
          })
          break
        case 'processing':
          eventBus.emit('modal.pending', { modalId, pending: true, timestamp: Date.now() })
          break
        case 'error':
          eventBus.emit('modal.pending', { modalId, pending: false, timestamp: Date.now() })
          break
        case 'success':
        case 'closed':
          eventBus.emit('modal.closed', {
            modalId,
            result: context?.data,
            timestamp: Date.now(),
          })
          break
      }
    },
  },
})

export { useModalManagerStore }
export default useModalManagerStore
